import React, { createContext, useState, useMemo, useContext } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ColorModeContext = createContext({ toggleColorMode: () => {} });

export const useColorMode = () => useContext(ColorModeContext);

export const ColorModeProvider = ({ children }) => {
  // Lire le mode depuis le localStorage ou utiliser 'light' par défaut
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('era_theme_mode');
    return savedMode || 'light';
  });

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === 'light' ? 'dark' : 'light';
          localStorage.setItem('era_theme_mode', newMode);
          return newMode;
        });
      },
      mode,
    }),
    [mode]
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: '#3498db',
            light: '#5dade2',
            dark: '#2874a6',
          },
          secondary: {
            main: '#27ae60',
            light: '#52be80',
            dark: '#1e8449',
          },
          ...(mode === 'light'
            ? {
                // Palette Light
                background: {
                  default: '#f8f9fa',
                  paper: '#ffffff',
                },
                text: {
                  primary: 'rgba(0, 0, 0, 0.87)',
                  secondary: 'rgba(0, 0, 0, 0.6)',
                },
              }
            : {
                // Palette Dark
                background: {
                  default: '#121212',
                  paper: '#1e1e1e',
                },
                text: {
                  primary: '#ffffff',
                  secondary: 'rgba(255, 255, 255, 0.7)',
                },
              }),
        },
        typography: {
          fontFamily: [
            'Inter',
            'Roboto',
            '-apple-system',
            'BlinkMacSystemFont',
            'Segoe UI',
            'Arial',
            'sans-serif',
          ].join(','),
          h1: { fontFamily: 'Inter', fontWeight: 700 },
          h2: { fontFamily: 'Inter', fontWeight: 600 },
          h3: { fontFamily: 'Inter', fontWeight: 600 },
          body1: { fontFamily: 'Roboto' },
        },
        shape: {
          borderRadius: 8,
        },
        components: {
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: mode === 'light' ? '#ffffff' : '#1e1e1e',
                        color: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : '#ffffff',
                    }
                }
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none', // Supprime l'overlay par défaut en mode dark
                    }
                }
            }
        }
      }),
    [mode]
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
